import { ConfigProvider } from "antd";
import { useApplicationViewSettings } from "../../../../../containers/ApplicationPage/ApplicationViewContext";
import React from "react";
import { useTranslation } from "react-i18next";
import { Button, Descriptions, Select, Space } from "antd";
import { useSelector } from "react-redux";
import { DeleteOutlined, MinusOutlined, PlusOutlined, PaperClipOutlined } from "@ant-design/icons";
import _ from "lodash";
import { makeSelectDangerousGoodsClasses, makeSelectHost, makeSelectLanguage } from "../../../../../containers/App/selectors";
import getDocumentUrl from "../../../../../utils/getDocumentUrl";
import UploadFileDragger from "../../../../UploadFileDragger";
import LPCustomNumericFormat from "../../../../LongpollControls/LPCustomNumericFormat";
import LPInput from "../../../../LongpollControls/LPInput";
import LPTextArea from "../../../../LongpollControls/LPTextArea";
import { AttachDocumentDataConverter } from "../../../../../viewModels/Documents/AttachDocumentModel";
import getLocalizedName from "../../../../../utils/getLocalizedName";
import type { ApplicationSummaryBlockProps } from "../../types";

const DangerousCargoInfo = React.memo(({ application, onChange, canEditApplicationFields, onCommit }: ApplicationSummaryBlockProps) => {
  const { t } = useTranslation("translations");
  const lang = useSelector(makeSelectLanguage());
  const viewSettings = useApplicationViewSettings();
  const host = useSelector(makeSelectHost());
  const dangerousGoodsClasses = useSelector(makeSelectDangerousGoodsClasses());
  const handleAddUnNumber = React.useCallback(
    (classIndex: number) => {
      const copy = _.cloneDeep(application);
      const dangerClass = copy.cargoInfo?.dangerousCargoInfo?.classesOfDanger?.[classIndex];
      if (!dangerClass) {
        return;
      }
      dangerClass.unNumbers = [...(dangerClass.unNumbers || []), {}];
      onChange(copy);
      onCommit?.();
    },
    [application, onChange, onCommit],
  );
  const handleAddClassOfDanger = React.useCallback(() => {
    if (onChange) {
      const classesOfDanger = _.cloneDeep(application.cargoInfo?.dangerousCargoInfo?.classesOfDanger || []);
      classesOfDanger.push({ unNumbers: [{}] });

      onChange({
        ...application,
        cargoInfo: {
          ...application.cargoInfo,
          dangerousCargoInfo: { ...application.cargoInfo?.dangerousCargoInfo, classesOfDanger },
        },
      });

      onCommit?.();
    }
  }, [onChange, application, onCommit]);
  const handleRemoveClassOfDanger = React.useCallback(
    (index: number) => {
      if (onChange) {
        const classesOfDanger = _.cloneDeep(application.cargoInfo?.dangerousCargoInfo?.classesOfDanger || []);
        classesOfDanger.splice(index, 1);
        onChange({
          ...application,
          cargoInfo: {
            ...application.cargoInfo,
            dangerousCargoInfo: { ...application.cargoInfo?.dangerousCargoInfo, classesOfDanger },
          },
        });

        onCommit?.();
      }
    },
    [onChange, application, onCommit],
  );
  const dangerousCargoLabel = application.cargoInfo?.dangerousCargoInfo?.isDangerous ? t("yes") : t("no");
  const classOfDangerLabel = application.cargoInfo?.dangerousCargoInfo?.isDangerous
    ? application.cargoInfo?.dangerousCargoInfo?.classOfDanger
    : "-";
  const dangerCargoDescriptionLabel = application.cargoInfo?.dangerousCargoInfo?.isDangerous
    ? application.cargoInfo?.dangerousCargoInfo?.description
    : "-";
  return (
    <Space orientation="vertical" style={{ width: "100%" }}>
      {application.type !== "FTL" && (
        <ConfigProvider
          theme={{
            token: { fontSize: 12 },
            components: {
              Select: {
                activeBorderColor: "transparent",
                activeOutlineColor: "transparent",
              },
              Input: {
                activeBorderColor: "transparent",
                activeShadow: "none",
                inputFontSize: 12,
                inputFontSizeSM: 12,
                inputFontSizeLG: 12,
                paddingBlock: 0,
                paddingInline: 0,
                paddingBlockSM: 0,
                paddingInlineSM: 0,
                paddingBlockLG: 0,
                paddingInlineLG: 0,
              },
              InputNumber: {
                activeBorderColor: "transparent",
                activeShadow: "none",
                inputFontSize: 12,
                inputFontSizeSM: 12,
                inputFontSizeLG: 12,
                paddingBlock: 0,
                paddingInline: 0,
                paddingBlockSM: 0,
                paddingInlineSM: 0,
                paddingBlockLG: 0,
                paddingInlineLG: 0,
              },
            },
          }}
          select={{ styles: { root: { padding: 0 } } }}
          textArea={{ styles: { textarea: { minHeight: 22 } } }}
          descriptions={{
            styles: {
              label: { padding: "6px 8px" },
              content: { padding: "6px 8px" },
            },
          }}
        >
          <Descriptions
            bordered
            items={[
              {
                label: t("danger_cargo"),
                styles: { label: { width: "13%" }, content: { width: "20%" } },
                children: canEditApplicationFields ? (
                  <Select
                    variant="borderless"
                    value={application.cargoInfo?.dangerousCargoInfo?.isDangerous}
                    options={[
                      {
                        value: true,
                        label: t("yes"),
                      },
                      {
                        value: false,
                        label: t("no"),
                      },
                    ]}
                    suffixIcon={null}
                    onChange={(v) => {
                      onChange({
                        ...application,
                        cargoInfo: {
                          ...application.cargoInfo,
                          dangerousCargoInfo: { ...application.cargoInfo?.dangerousCargoInfo, isDangerous: v },
                        },
                      });
                      onCommit?.();
                    }}
                    style={{ width: "100%" }}
                  />
                ) : (
                  dangerousCargoLabel
                ),
              },
              {
                label: t("class_of_danger"),
                styles: { label: { width: "13%" }, content: { width: "20%" } },
                children:
                  canEditApplicationFields && application.cargoInfo?.dangerousCargoInfo?.isDangerous ? (
                    <LPInput
                      value={application.cargoInfo?.dangerousCargoInfo?.classOfDanger}
                      variant="borderless"
                      onChange={(e) => {
                        onChange({
                          ...application,
                          cargoInfo: {
                            ...application.cargoInfo,
                            dangerousCargoInfo: { ...application.cargoInfo?.dangerousCargoInfo, classOfDanger: e.target.value },
                          },
                        });
                        onCommit?.();
                      }}
                    />
                  ) : (
                    classOfDangerLabel
                  ),
              },
              {
                label: t("passport_msds"),
                styles: { label: { width: "13%" }, content: { width: "20%" } },
                children: (
                  <>
                    {canEditApplicationFields && (
                      <UploadFileDragger
                        styles={{ trigger: { border: 0, background: "transparent", textAlign: "left" } }}
                        showUploadList={false}
                        multiple
                        onAttachFiles={(files) => {
                          onChange({
                            ...application,
                            cargoInfo: {
                              ...application.cargoInfo,
                              dangerousCargoInfo: {
                                ...application.cargoInfo?.dangerousCargoInfo,
                                documents: (application.cargoInfo?.dangerousCargoInfo?.documents || []).concat(
                                  AttachDocumentDataConverter.convertDocumentsToFormData(
                                    files.map((x) => ({ id: undefined!, documentId: x.id, documentName: x.name })),
                                    host,
                                  ),
                                ),
                              },
                            },
                          });
                          onCommit?.();
                        }}
                        content={
                          <Button size="small" icon={<PaperClipOutlined />} tabIndex={-1}>
                            {t("attach_files")}
                          </Button>
                        }
                      />
                    )}
                    <Space orientation="vertical" size={2}>
                      {application.cargoInfo?.dangerousCargoInfo?.documents &&
                        application.cargoInfo?.dangerousCargoInfo?.documents.map((x, index) => (
                          <Space key={index} align="center">
                            {viewSettings ? (
                              <span>{x.name}</span>
                            ) : (
                              <a target="_blank" href={getDocumentUrl(x.response?.id!, host)} rel="noreferrer">
                                {x.name}
                              </a>
                            )}
                            {canEditApplicationFields && (
                              <Button
                                icon={<DeleteOutlined style={{ fontSize: 12 }} />}
                                danger
                                type="text"
                                style={{ height: 16, width: 16 }}
                                onClick={() => {
                                  const copy = _.clone(application.cargoInfo?.dangerousCargoInfo?.documents!);
                                  copy.splice(index, 1);

                                  onChange({
                                    ...application,
                                    cargoInfo: {
                                      ...application.cargoInfo,
                                      dangerousCargoInfo: {
                                        ...application.cargoInfo?.dangerousCargoInfo,
                                        documents: copy,
                                      },
                                    },
                                  });
                                  onCommit?.();
                                }}
                              />
                            )}
                          </Space>
                        ))}
                    </Space>
                  </>
                ),
              },
              {
                label: t("danger_cargo_description"),
                children:
                  canEditApplicationFields && application.cargoInfo?.dangerousCargoInfo?.isDangerous ? (
                    <LPTextArea
                      autoSize
                      value={application.cargoInfo?.dangerousCargoInfo?.description}
                      variant="borderless"
                      onChange={(e) => {
                        onChange({
                          ...application,
                          cargoInfo: {
                            ...application.cargoInfo,
                            dangerousCargoInfo: { ...application.cargoInfo?.dangerousCargoInfo, description: e.target.value },
                          },
                        });
                        onCommit?.();
                      }}
                    />
                  ) : (
                    dangerCargoDescriptionLabel
                  ),
              },
            ]
              .flat(3)
              .filter((item) => !!item)}
          />
        </ConfigProvider>
      )}
      {application.type === "FTL" && (
        <ConfigProvider
          theme={{
            token: { fontSize: 12 },
            components: {
              Select: {
                activeBorderColor: "transparent",
                activeOutlineColor: "transparent",
              },
              Input: {
                activeBorderColor: "transparent",
                activeShadow: "none",
                inputFontSize: 12,
                inputFontSizeSM: 12,
                inputFontSizeLG: 12,
                paddingBlock: 0,
                paddingInline: 0,
                paddingBlockSM: 0,
                paddingInlineSM: 0,
                paddingBlockLG: 0,
                paddingInlineLG: 0,
              },
              InputNumber: {
                activeBorderColor: "transparent",
                activeShadow: "none",
                inputFontSize: 12,
                inputFontSizeSM: 12,
                inputFontSizeLG: 12,
                paddingBlock: 0,
                paddingInline: 0,
                paddingBlockSM: 0,
                paddingInlineSM: 0,
                paddingBlockLG: 0,
                paddingInlineLG: 0,
              },
            },
          }}
          select={{ styles: { root: { padding: 0 } } }}
          textArea={{ styles: { textarea: { minHeight: 22 } } }}
          descriptions={{
            styles: {
              label: { padding: "6px 8px" },
              content: { padding: "6px 8px" },
            },
          }}
        >
          <Descriptions
            bordered
            items={[
              {
                label: t("danger_cargo"),
                styles: { label: { width: "13%" }, content: { width: "20%" } },
                children: canEditApplicationFields ? (
                  <Select
                    variant="borderless"
                    value={application.cargoInfo?.dangerousCargoInfo?.isDangerous}
                    options={[
                      {
                        value: true,
                        label: t("yes"),
                      },
                      {
                        value: false,
                        label: t("no"),
                      },
                    ]}
                    suffixIcon={null}
                    onChange={(v) => {
                      onChange({
                        ...application,
                        cargoInfo: {
                          ...application.cargoInfo,
                          dangerousCargoInfo: {
                            ...application.cargoInfo?.dangerousCargoInfo,
                            isDangerous: v,
                            classesOfDanger: v ? [{ unNumbers: [{}] }] : [],
                          },
                        },
                      });
                      onCommit?.();
                    }}
                    style={{ width: "100%" }}
                  />
                ) : (
                  dangerousCargoLabel
                ),
              },
              {
                label: t("passport_msds"),
                styles: { label: { width: "13%" }, content: { width: "20%" } },
                children: (
                  <>
                    {canEditApplicationFields && (
                      <UploadFileDragger
                        styles={{ trigger: { border: 0, background: "transparent", textAlign: "left" } }}
                        showUploadList={false}
                        multiple
                        onAttachFiles={(files) => {
                          onChange({
                            ...application,
                            cargoInfo: {
                              ...application.cargoInfo,
                              dangerousCargoInfo: {
                                ...application.cargoInfo?.dangerousCargoInfo,
                                documents: (application.cargoInfo?.dangerousCargoInfo?.documents || []).concat(
                                  AttachDocumentDataConverter.convertDocumentsToFormData(
                                    files.map((x) => ({ id: undefined!, documentId: x.id, documentName: x.name })),
                                    host,
                                  ),
                                ),
                              },
                            },
                          });
                          onCommit?.();
                        }}
                        content={
                          <Button size="small" icon={<PaperClipOutlined />} tabIndex={-1}>
                            {t("attach_files")}
                          </Button>
                        }
                      />
                    )}
                    <Space orientation="vertical" size={2}>
                      {application.cargoInfo?.dangerousCargoInfo?.documents &&
                        application.cargoInfo?.dangerousCargoInfo?.documents.map((x, index) => (
                          <Space key={index} align="center">
                            {viewSettings ? (
                              <span>{x.name}</span>
                            ) : (
                              <a target="_blank" href={getDocumentUrl(x.response?.id!, host)} rel="noreferrer">
                                {x.name}
                              </a>
                            )}
                            {canEditApplicationFields && (
                              <Button
                                icon={<DeleteOutlined style={{ fontSize: 12 }} />}
                                danger
                                type="text"
                                style={{ height: 16, width: 16 }}
                                onClick={() => {
                                  const copy = _.clone(application.cargoInfo?.dangerousCargoInfo?.documents!);
                                  copy.splice(index, 1);

                                  onChange({
                                    ...application,
                                    cargoInfo: {
                                      ...application.cargoInfo,
                                      dangerousCargoInfo: {
                                        ...application.cargoInfo?.dangerousCargoInfo,
                                        documents: copy,
                                      },
                                    },
                                  });
                                  onCommit?.();
                                }}
                              />
                            )}
                          </Space>
                        ))}
                    </Space>
                  </>
                ),
              },
              {
                label: t("danger_cargo_description"),
                children:
                  canEditApplicationFields && application.cargoInfo?.dangerousCargoInfo?.isDangerous ? (
                    <LPTextArea
                      autoSize
                      value={application.cargoInfo?.dangerousCargoInfo?.description}
                      variant="borderless"
                      onChange={(e) => {
                        onChange({
                          ...application,
                          cargoInfo: {
                            ...application.cargoInfo,
                            dangerousCargoInfo: { ...application.cargoInfo?.dangerousCargoInfo, description: e.target.value },
                          },
                        });
                        onCommit?.();
                      }}
                    />
                  ) : (
                    dangerCargoDescriptionLabel
                  ),
              },
            ]
              .flat(3)
              .filter((item) => !!item)}
          />
        </ConfigProvider>
      )}
      {application.type === "FTL" && canEditApplicationFields && application.cargoInfo?.dangerousCargoInfo?.isDangerous && (
        <Button size="small" type="text" icon={<PlusOutlined />} onClick={handleAddClassOfDanger}>
          {t("add_class_of_danger")}
        </Button>
      )}
      {application.type === "FTL" && application.cargoInfo?.dangerousCargoInfo?.isDangerous && (
        <ConfigProvider
          theme={{
            token: { fontSize: 12 },
            components: {
              Select: {
                activeBorderColor: "transparent",
                activeOutlineColor: "transparent",
              },
              Input: {
                activeBorderColor: "transparent",
                activeShadow: "none",
                inputFontSize: 12,
                inputFontSizeSM: 12,
                inputFontSizeLG: 12,
                paddingBlock: 0,
                paddingInline: 0,
                paddingBlockSM: 0,
                paddingInlineSM: 0,
                paddingBlockLG: 0,
                paddingInlineLG: 0,
              },
              InputNumber: {
                activeBorderColor: "transparent",
                activeShadow: "none",
                inputFontSize: 12,
                inputFontSizeSM: 12,
                inputFontSizeLG: 12,
                paddingBlock: 0,
                paddingInline: 0,
                paddingBlockSM: 0,
                paddingInlineSM: 0,
                paddingBlockLG: 0,
                paddingInlineLG: 0,
              },
            },
          }}
          select={{ styles: { root: { padding: 0 } } }}
          textArea={{ styles: { textarea: { minHeight: 22 } } }}
          descriptions={{
            styles: {
              label: { padding: "6px 8px" },
              content: { padding: "6px 8px" },
            },
          }}
        >
          <Descriptions
            bordered
            items={[
              application.cargoInfo?.dangerousCargoInfo?.isDangerous &&
                application.cargoInfo.dangerousCargoInfo.classesOfDanger?.map((x, index) => [
                  {
                    label: t("class_of_danger"),
                    styles: { label: { width: "13%" }, content: { width: "30%" } },
                    children: canEditApplicationFields ? (
                      <Select
                        variant="borderless"
                        value={x.dangerousGoodsClassId}
                        options={dangerousGoodsClasses.map((x) => ({
                          value: x.id,
                          label: `${t("class", { number: x.classNumber })} ${getLocalizedName(x, lang)}`,
                          option: x,
                        }))}
                        suffixIcon={null}
                        style={{ width: "100%" }}
                        showSearch={{
                          filterOption: (input, option) => (option?.option.classNumber ?? "").toLowerCase().includes(input.toLowerCase()),
                        }}
                        onChange={(v) => {
                          const copy = _.cloneDeep(application);
                          if (copy.cargoInfo && copy.cargoInfo.dangerousCargoInfo && copy.cargoInfo.dangerousCargoInfo.classesOfDanger) {
                            copy.cargoInfo.dangerousCargoInfo.classesOfDanger[index].dangerousGoodsClassId = v;
                            onChange(copy);

                            onCommit?.();
                          }
                        }}
                      />
                    ) : x.dangerousGoodsClassId ? (
                      getLocalizedName(dangerousGoodsClasses.find((d) => d.id === x.dangerousGoodsClassId)!, lang)
                    ) : (
                      "-"
                    ),
                  },
                  {
                    label: t("un_code"),
                    children: (
                      <Space orientation="vertical" size={0}>
                        {canEditApplicationFields && x.unNumbers.length === 0 && (
                          <Button size="small" type="text" icon={<PlusOutlined />} onClick={() => handleAddUnNumber(index)}>
                            {t("un_code")}
                          </Button>
                        )}
                        {x.unNumbers.map((u, uIndex) =>
                          canEditApplicationFields ? (
                            <Space key={uIndex}>
                              {uIndex === 0 ? (
                                <PlusOutlined onClick={() => handleAddUnNumber(index)} />
                              ) : (
                                <DeleteOutlined
                                  onClick={() => {
                                    const copy = _.cloneDeep(application);
                                    if (
                                      copy.cargoInfo &&
                                      copy.cargoInfo.dangerousCargoInfo &&
                                      copy.cargoInfo.dangerousCargoInfo &&
                                      copy.cargoInfo.dangerousCargoInfo.classesOfDanger
                                    ) {
                                      copy.cargoInfo.dangerousCargoInfo.classesOfDanger[index].unNumbers.splice(uIndex, 1);
                                    }
                                    onChange(copy);
                                    onCommit?.();
                                  }}
                                />
                              )}
                              <LPCustomNumericFormat
                                value={u.unNumber ? Number(u.unNumber) : undefined}
                                variant="borderless"
                                maxLength={4}
                                allowNegative={false}
                                decimalScale={0}
                                onChange={(v) => {
                                  const copy = _.cloneDeep(application);
                                  if (
                                    copy.cargoInfo &&
                                    copy.cargoInfo.dangerousCargoInfo &&
                                    copy.cargoInfo.dangerousCargoInfo &&
                                    copy.cargoInfo.dangerousCargoInfo.classesOfDanger
                                  ) {
                                    copy.cargoInfo.dangerousCargoInfo.classesOfDanger[index].unNumbers[uIndex].unNumber = v?.toString();
                                    onChange(copy);
                                    onCommit?.();
                                  }
                                }}
                              />
                            </Space>
                          ) : (
                            u.unNumber
                          ),
                        )}
                      </Space>
                    ),
                  },
                  canEditApplicationFields
                    ? {
                        key: `delete${index}`,
                        styles: { label: { width: 0, padding: 0, borderInlineEnd: 0 }, content: { width: 32 } },
                        children: (
                          <Button
                            onClick={() => handleRemoveClassOfDanger(index)}
                            type="text"
                            size="small"
                            icon={<MinusOutlined />}
                            aria-label="Удалить груз"
                            disabled={index === 0}
                          />
                        ),
                      }
                    : {
                        key: `delete${index}`,
                        styles: { label: { width: 0, padding: 0, borderInlineEnd: 0 }, content: { width: 32 } },
                        children: "-",
                      },
                ]),
            ]
              .flat(3)
              .filter((item) => !!item)}
          />
        </ConfigProvider>
      )}
    </Space>
  );
});
export default DangerousCargoInfo;
